import { SignalStore } from '@awork/core/state/signal-store/signalStore'
import { Injectable } from '@angular/core'
import type { ISOLanguage } from '@awork/_shared/models/account.model'
import type { HttpErrorResponse } from '@angular/common/http'

interface RequestErrorInfo {
  message: string
  error: HttpErrorResponse | Error
  traceId: string
  date: Date
}

export interface FailedRequestDiagnosticsStateEntry {
  durationMs: number | null
  method: string
  status: number
  timestamp: string
  traceId: string | null
  url: string
}

export interface AppState {
  traceIds: string[]
  traceIdsError: string[]
  failedRequests: FailedRequestDiagnosticsStateEntry[]
  versionCheck: Date
  checkingVersion: boolean
  language: ISOLanguage
  isLoggedIn: boolean
  referralCode?: string
  lastSyncDate?: Date
  isSwitchingWorkspace: boolean
  pushNotificationRegistrationId: string
  isMSTeams: boolean
  debug: boolean
  cacheWiped?: Date
  lastRoute: string
  adBlockerErrorOccurred: boolean
  /**
   * Legacy setting for opening links in the desktop app.
   * @deprecated Migrated to SettingsStore.desktopApp. Kept for backward-compatible IndexedDB migration.
   */
  openLinksInDesktopApp?: boolean
  logoutReason?: Partial<RequestErrorInfo>
  refreshSessionError?: Partial<RequestErrorInfo>
}

export function createInitialState(): AppState {
  return {
    traceIds: [],
    traceIdsError: [],
    failedRequests: [],
    versionCheck: null,
    checkingVersion: false,
    language: 'en-GB',
    isLoggedIn: false,
    referralCode: null,
    lastSyncDate: null,
    isSwitchingWorkspace: false,
    pushNotificationRegistrationId: null,
    isMSTeams: false,
    debug: false,
    cacheWiped: null,
    lastRoute: null,
    adBlockerErrorOccurred: false,
    openLinksInDesktopApp: null
  }
}

@Injectable({ providedIn: 'root' })
export class AppStore extends SignalStore<AppState> {
  constructor() {
    super({
      name: 'app',
      initialState: createInitialState()
    })
  }

  /**
   * Sets localStorage key 'awLogin' used to show the proper App's loader
   * @param {Readonly<AppState>} prevState
   * @param {Readonly<AppState>} nextState
   * @returns {Partial<AppState>}
   */
  preUpdateState(prevState: Readonly<AppState>, nextState: Readonly<AppState>): Partial<AppState> {
    localStorage.setItem('awLogin', nextState.isLoggedIn ? 'true' : 'false')
    return super.preUpdateState(prevState, nextState)
  }

  logTrace(traceId: string, error: boolean): void {
    const traceLimit = 100

    this.update(appState => {
      if (error) {
        const traceIds = appState.traceIdsError

        if (traceIds.length === traceLimit) {
          traceIds.shift()
        }

        return { traceIdsError: [...traceIds, traceId] }
      } else {
        const traceIds = appState.traceIds

        if (traceIds.length === traceLimit) {
          traceIds.shift()
        }

        return { traceIds: [...traceIds, traceId] }
      }
    })
  }

  /**
   * Set the last sync date
   * @param {Date} date
   */
  setLastSyncDate(date: Date): void {
    this.update({ lastSyncDate: date })
  }

  /**
   * Set the registration id for push notifications
   * @param {string} registrationId
   */
  setPushNotificationRegistrationId(registrationId: string): void {
    this.update({ pushNotificationRegistrationId: registrationId })
  }

  /**
   * Sets the isMSTeams flag
   * @param {boolean} flag
   */
  setIsMSTeams(flag: boolean): void {
    this.update({ isMSTeams: flag })
  }

  /**
   * Sets the debug flag
   * @param {boolean} debug
   */
  setDebug(debug: boolean): void {
    this.update({ debug })
  }

  /**
   * Sets the last time the cache were wiped
   * @param {Date} date
   */
  setCacheWiped(date: Date): void {
    this.update({ cacheWiped: date })
  }

  /**
   * Sets the last rendered route
   * @param {string} route
   */
  setLastRoute(route: string): void {
    this.update({ lastRoute: route })
  }

  /**
   * Sets the flag used to show to the user the API error caused by AdBlockers
   * @param {boolean} errorOccurred
   */
  setAdBlockerErrorOccurred(errorOccurred: boolean): void {
    this.update({ adBlockerErrorOccurred: errorOccurred })
  }

  /**
   * Sets the flag used to determine if the app is checking the version
   * @param {boolean} checking
   */
  setCheckingVersion(checking: boolean): void {
    this.update({ checkingVersion: checking })
  }

  /**
   * Stores the legacy setting for opening links in the desktop app.
   * @param {boolean} openLinksInDesktopApp
   * @deprecated Migrated to SettingsStore.desktopApp. Kept for backward-compatible IndexedDB migration.
   */
  setOpenLinksInDesktopApp(openLinksInDesktopApp: boolean): void {
    this.update({ openLinksInDesktopApp })
  }

  /**
   * Clears the legacy setting for opening links in the desktop app.
   * @deprecated Migrated to SettingsStore.desktopApp. Kept for backward-compatible IndexedDB migration.
   */
  clearOpenLinksInDesktopApp(): void {
    this.update({ openLinksInDesktopApp: null })
  }

  /**
   * Sets the reason why the user was logged out
   * @param {Partial<RequestErrorInfo>} errorInfo
   */
  setLogoutReason(errorInfo: Partial<RequestErrorInfo>): void {
    this.update({ logoutReason: errorInfo })
  }

  /**
   * Sets the refresh session error
   * @param {Partial<RequestErrorInfo>} errorInfo
   */
  setRefreshSessionError(errorInfo: Partial<RequestErrorInfo>): void {
    this.update({ refreshSessionError: errorInfo })
  }
}
